import { useState, useMemo, memo, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

import { ArrowRight, Play, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import Hero3DVisualization from "./Hero3DVisualization";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import DemoVideoModal from "./DemoVideoModal";
const HeroSection = memo(() => {
  const { t } = useLanguage();
  const { settings } = useSiteSettings();
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const isDemoEnabled = settings.demo_video_enabled && 
    ((settings.demo_video_type === 'youtube' && settings.demo_video_youtube_url) ||
     (settings.demo_video_type === 'upload' && settings.demo_video_upload_url));

  const benefits = useMemo(() => [
    t("hero.benefit1"),
    t("hero.benefit2"),
    t("hero.benefit3"),
  ], [t]);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { 
        duration: 0.6,
        ease: "easeOut" as const
      }
    }
  };

  return (
    <section ref={sectionRef} className="relative min-h-screen flex items-center overflow-hidden bg-transparent">

      <motion.div 
        className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16 relative z-10"
      >
        <motion.div 
          className="flex flex-col items-center gap-12"
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? "visible" : "hidden"}
        >
          {/* Top Content - Text */}
          <div className="text-center max-w-4xl">
            {/* Headline */}
            <motion.h1 
              variants={itemVariants}
              className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6"
            >
              {t("hero.title1")}{" "}
              <span className="gradient-text relative inline-block pb-2">
                {t("hero.title2")}
                <motion.span 
                  className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-secondary rounded-full origin-left"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 0.8, duration: 0.6 }}
                />
              </span>{" "}
              {t("hero.title3")}
            </motion.h1>

            {/* Subheadline */}
            <motion.p 
              variants={itemVariants}
              className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8"
            >
              {t("hero.subtitle")}
              <strong className="text-foreground"> {t("hero.subtitleBold")}</strong>
            </motion.p>

            {/* CTA Buttons */}
            <motion.div 
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4 justify-center mb-8"
            >
              <Button variant="hero" size="xl" asChild className="group relative overflow-hidden">
                <Link to="/signup">
                  <span className="relative z-10 flex items-center">
                    {t("hero.cta")}
                    <ArrowRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
                  </span>
                  <motion.span 
                    className="absolute inset-0 bg-gradient-to-r from-secondary to-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  />
                </Link>
              </Button>
              {isDemoEnabled && (
                <Button 
                  variant="glass" 
                  size="xl"
                  onClick={() => setIsDemoModalOpen(true)}
                  className="group"
                >
                  <Play className="w-5 h-5 mr-1 group-hover:scale-110 transition-transform" />
                  {t("hero.demo")}
                </Button>
              )}
            </motion.div>

            {/* Trust Badges */}
            <motion.div 
              variants={itemVariants}
              className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-4 justify-center items-center"
            >
              {benefits.map((benefit, index) => (
                <motion.span 
                  key={benefit} 
                  className="flex items-center gap-2 text-sm text-muted-foreground"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1 + index * 0.1 }}
                >
                  <motion.div 
                    className="w-6 h-6 rounded-full bg-gradient-to-br from-success to-success/80 flex items-center justify-center flex-shrink-0 shadow-md"
                    whileHover={{ scale: 1.2, rotate: 10 }}
                    transition={{ type: "spring", stiffness: 400 }}
                  >
                    <Check className="w-3.5 h-3.5 text-white drop-shadow-sm" />
                  </motion.div>
                  {benefit}
                </motion.span>
              ))}
            </motion.div>
          </div>

          {/* Bottom Content - Premium 3D Automation Visualization */}
          <motion.div
            variants={itemVariants}
            className="relative w-full"
          >
            {/* 3D Visualization */}
            <Hero3DVisualization />
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div 
        className="absolute bottom-4 left-1/2 -translate-x-1/2"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5 }}
      >
        <motion.div 
          className="w-6 h-10 rounded-full border-2 border-muted-foreground/30 flex justify-center pt-2"
          animate={{ y: [0, 5, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <motion.div 
            className="w-1.5 h-3 rounded-full bg-gradient-to-b from-primary to-secondary"
            animate={{ y: [0, 8, 0], opacity: [1, 0.5, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        </motion.div>
      </motion.div>
      
      {/* Demo Video Modal */}
      <DemoVideoModal open={isDemoModalOpen} onOpenChange={setIsDemoModalOpen} />
    </section>
  );
});

HeroSection.displayName = "HeroSection";

export default HeroSection;